import { supabase, getSupabaseUrl } from './supabase';
import { CanonicalProject, ReellyProjectListItem, ReellyProjectDetail, UnitTypeOption, PaymentPlan } from '../types/canonicalProject';
import { calculateROI } from './roi-calculator';

export const PAGE_SIZE = 24;
const BULK_FETCH_LIMIT = 100;

export interface ProjectParams {
    page?: number;
    limit?: number;
    search_query?: string;
    unit_price_from?: number;
    unit_price_to?: number;
    bedrooms?: string; // e.g., "1,2"
    completion_date_from?: number;
    completion_date_to?: number;
    developer?: string;
    location?: string;
    ordering?: string;
    property_types?: string[];
    locations?: string[];
    sqft_min?: number;
    sqft_max?: number;
    ppsf_min?: number;
    ppsf_max?: number;
    sale_status?: string | string[]; // Filter by sale_status (e.g., "Presale", "Start Of Sales")
}

export interface ReellyProject {
    id: string;
    name: string;
    developer: {
        name: string;
        logo?: string;
    };
    location: {
        city: string;
        district: string;
    };
    cover_image: string;
    min_price: number;
    max_price: number;
    completion_date: string;
    status: string;
}

export interface PaginatedResponse<T> {
    items: T[];
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
}

export const MarketplaceAPI = {
    /**
     * Fetch paginated list of projects from Canonical DB (Supabase)
     */
    getProjects: async (params: ProjectParams = {}): Promise<PaginatedResponse<CanonicalProject>> => {
        const page = Math.max(1, params.page || 1);
        const limit = params.limit || PAGE_SIZE;
        const offset = (page - 1) * limit;

        let query = supabase
            .from('projects_canonical')
            .select('*', { count: 'exact' });

        if (params.search_query) {
            // Escape special characters for Postgres ILIKE
            const q = params.search_query
                .replace(/\\/g, '\\\\')
                .replace(/%/g, '\\%')
                .replace(/_/g, '\\_')
                .replace(/\[/g, '\\[');
            // Search across title, location, developer, district, and description
            query = query.or(`title.ilike.%${q}%,location_display.ilike.%${q}%,developer.ilike.%${q}%,district.ilike.%${q}%,description.ilike.%${q}%`);
        }

        if (params.unit_price_from) {
            query = query.gte('price_rounded_aed', params.unit_price_from);
        }
        if (params.unit_price_to) {
            query = query.lte('price_rounded_aed', params.unit_price_to);
        }

        if (params.bedrooms) {
            const beds = params.bedrooms.split(',').map(b => parseInt(b, 10)).filter(n => !Number.isNaN(n));
            if (beds.length > 0) {
                const minBed = Math.min(...beds);
                const maxBed = Math.max(...beds);
                query = query.gte('bedrooms_max', minBed).lte('bedrooms_min', maxBed);
            }
        }

        if (params.completion_date_from) {
            query = query.gte('completion_timestamp', new Date(params.completion_date_from).toISOString());
        }
        if (params.completion_date_to) {
            query = query.lte('completion_timestamp', new Date(params.completion_date_to).toISOString());
        }

        if (params.developer) {
            query = query.ilike('developer', `%${params.developer}%`);
        }
        if (params.location) {
            query = query.ilike('district', `%${params.location}%`);
        }
        if (params.locations && params.locations.length > 0) {
            query = query.in('district', params.locations);
        }
        if (params.property_types && params.property_types.length > 0) {
            // Robust property type filtering: check primary type OR if the types array contains the selected types
            const typeConditions = params.property_types.map(t => {
                const escapedT = t.replace(/'/g, "''");
                return `property_type_primary.eq."${escapedT}",property_type_types.cs.{${escapedT}}`;
            }).join(',');
            query = query.or(typeConditions);
        }
        if (params.sqft_min && params.sqft_min > 0) {
            query = query.gte('sqft_avg', params.sqft_min);
        }
        if (params.sqft_max && params.sqft_max > 0) {
            query = query.lte('sqft_avg', params.sqft_max);
        }
        if (params.ppsf_min && params.ppsf_min > 0) {
            query = query.gte('price_per_sqft', params.ppsf_min);
        }
        if (params.ppsf_max && params.ppsf_max > 0) {
            query = query.lte('price_per_sqft', params.ppsf_max);
        }

        // Filter by sale_status (for hot projects / featured projects)
        if (params.sale_status) {
            const statuses = Array.isArray(params.sale_status) ? params.sale_status : [params.sale_status];
            if (statuses.length > 0) {
                // Normalize status values to match database format
                const normalizedStatuses = statuses.map(s => {
                    const normalized = s.trim();
                    // Handle variations: "Presale", "Start Of Sales", "Start of Sales", etc.
                    if (normalized.toLowerCase() === 'presale') return 'Presale';
                    if (normalized.toLowerCase().includes('start') && normalized.toLowerCase().includes('sales')) {
                        return 'Start Of Sales';
                    }
                    return normalized;
                });
                query = query.in('sale_status', normalizedStatuses);
            }
        }

        if (params.ordering) {
            if (params.ordering === 'unit_price_from') {
                query = query.order('price_rounded_aed', { ascending: true, nullsFirst: false });
            } else if (params.ordering === '-unit_price_from') {
                query = query.order('price_rounded_aed', { ascending: false, nullsFirst: false });
            } else if (params.ordering === '-source_created_at' || params.ordering === '-created_at') {
                // Sort by date (newest first)
                // Reelly API doesn't have created_at, so use updated_at as primary, fallback to last_sync
                query = query.order('source_updated_at', { ascending: false, nullsFirst: false })
                    .order('last_sync', { ascending: false, nullsFirst: false });
            } else {
                // Default fallback: same as date sort
                query = query.order('source_updated_at', { ascending: false, nullsFirst: false })
                    .order('last_sync', { ascending: false, nullsFirst: false });
            }
        } else {
            // Default: Show newest projects first (by source_updated_at - when project was last updated in Reelly)
            // Reelly API doesn't provide created_at, so we use updated_at
            query = query.order('source_updated_at', { ascending: false, nullsFirst: false })
                .order('last_sync', { ascending: false, nullsFirst: false });
        }

        if (import.meta.env.DEV) {
            console.group('[MarketplaceAPI] getProjects');
            console.log('Params:', { limit, offset, page, ...params });
        }

        const { data, count, error } = await query.range(offset, offset + limit - 1);
        if (error) throw error;

        if (import.meta.env.DEV) {
            console.log('Query result:', { 
                fetched: data?.length || 0, 
                total: count,
                range: `${offset}-${offset + limit - 1}`
            });
            console.groupEnd();
        }

        // Fetch developer logos from developers table
        const developerNames = [...new Set((data || []).map((row: any) => row.developer).filter(Boolean))];
        const developerLogoMap = new Map<string, string>();
        
        if (developerNames.length > 0) {
            // Fetch all developers and create a case-insensitive map
            const { data: developers } = await supabase
                .from('developers')
                .select('name, logo_url_256, logo_url_64');
            
            if (developers && developers.length > 0) {
                // Create a case-insensitive lookup map
                const devMap = new Map<string, { name: string; logo: string | null }>();
                developers.forEach((dev: any) => {
                    const logo = dev.logo_url_256 || dev.logo_url_64 || null;
                    const nameLower = dev.name.toLowerCase();
                    devMap.set(nameLower, { name: dev.name, logo });
                });
                
                // Match developer names case-insensitively
                developerNames.forEach((devName: string) => {
                    const devLower = devName.toLowerCase();
                    const match = devMap.get(devLower);
                    if (match && match.logo) {
                        developerLogoMap.set(devName, match.logo);
                    }
                });
                
                if (import.meta.env.DEV) {
                    console.log('[MarketplaceAPI] Developer logos loaded:', {
                        requested: developerNames.length,
                        found: developerLogoMap.size,
                        logos: Array.from(developerLogoMap.entries()).map(([name, logo]) => ({ name, hasLogo: !!logo }))
                    });
                }
            }
        }

        const items = (data || []).map((row: any) => {
            const project = mapCanonicalRowToProject(row);
            // Override developer logo with logo from developers table if available
            if (row.developer && developerLogoMap.has(row.developer)) {
                project.developerLogo = developerLogoMap.get(row.developer);
            }
            return project;
        });
        const total = count || 0;

        return {
            items,
            page,
            pageSize: limit,
            total,
            totalPages: Math.ceil(total / limit)
        };
    },

    /**
     * Fetch all projects across pages for full client-side sorting.
     * (Note: canonical DB supports global sorting so prefer server-side.)
     */
    getAllProjects: async (params: ProjectParams = {}): Promise<PaginatedResponse<CanonicalProject>> => {
        const perPage = Math.min(params.limit || BULK_FETCH_LIMIT, BULK_FETCH_LIMIT);
        const firstPage = await MarketplaceAPI.getProjects({ ...params, page: 1, limit: perPage });
        const allItems = [...firstPage.items];

        for (let page = 2; page <= firstPage.totalPages; page++) {
            const res = await MarketplaceAPI.getProjects({ ...params, page, limit: perPage });
            allItems.push(...res.items);
        }

        return {
            items: allItems,
            page: 1,
            pageSize: perPage,
            total: firstPage.total,
            totalPages: firstPage.totalPages
        };
    },

    /**
     * Fetch detailed project info by ID or slug and return canonical model
     */
    getProjectDetails: async (idOrSlug: string): Promise<CanonicalProject | null> => {
        try {
            // Try to fetch by slug first (if it looks like a slug, not a numeric ID)
            const isNumericId = /^\d+$/.test(idOrSlug);
            let query = supabase.from('projects_canonical').select('*');
            
            if (isNumericId) {
                // Numeric ID - query by id
                query = query.eq('id', idOrSlug);
            } else {
                // Likely a slug - try slug first, fallback to id
                query = query.or(`slug.eq.${idOrSlug},id.eq.${idOrSlug}`);
            }
            
            const { data: row, error } = await query.maybeSingle(); // Use maybeSingle() instead of single() to avoid error when no rows
            
            // Fetch developer logo if developer name exists
            let developerLogo = row?.developer_logo || undefined;
            if (row?.developer) {
                // Fetch all developers and match case-insensitively
                const { data: developers } = await supabase
                    .from('developers')
                    .select('name, logo_url_256, logo_url_64');
                
                if (developers && developers.length > 0) {
                    const devNameLower = row.developer.toLowerCase();
                    const match = developers.find((dev: any) => dev.name.toLowerCase() === devNameLower);
                    if (match) {
                        developerLogo = match.logo_url_256 || match.logo_url_64 || undefined;
                    }
                }
            }
            
            if (error) {
                console.error('[MarketplaceAPI] Error fetching project details:', {
                    error,
                    errorCode: error.code,
                    errorMessage: error.message,
                    errorDetails: error.details,
                    errorHint: error.hint,
                    idOrSlug,
                    idType: typeof idOrSlug
                });
                return null;
            }
            
            if (!row) {
                console.warn('[MarketplaceAPI] Project not found for ID/slug:', idOrSlug);
                return null;
            }

            const projectId = row.id; // Use the actual project ID from the row

            const { data: media } = await supabase
                .from('project_media')
                .select('url, media_type, sort_order')
                .eq('project_id', projectId)
                .order('sort_order', { ascending: true });

            const { data: plans } = await supabase
                    .from('project_payment_plans')
                    .select('*')
                    .eq('project_id', projectId)
                    .order('created_at', { ascending: false });

            const { data: signals } = await supabase
                .from('project_unit_signals')
                .select('*')
                .eq('project_id', projectId);

            // Use the project's actual price for unit types instead of hardcoding 'POA'
            const projectPriceNumeric = row.price_rounded_aed || 0;
            const projectPriceDisplay = projectPriceNumeric > 0 ? `AED ${Math.floor(projectPriceNumeric).toLocaleString()}` : 'Price on Request';
            
            const unitTypes: UnitTypeOption[] = (signals || []).map((s: any) => {
                const typeName = s.unit_type
                    ? `${String(s.unit_type).charAt(0).toUpperCase()}${String(s.unit_type).slice(1)}${s.bedrooms ? ` ${s.bedrooms}BR` : ''}`
                    : `Unit ${s.bedrooms ? `${s.bedrooms}BR` : ''}`.trim();
                return {
                    type: typeName,
                    beds: s.bedrooms || 0,
                    sqft: 'TBD',
                    price: projectPriceDisplay, // Use actual project price instead of hardcoded 'POA'
                    priceNumeric: projectPriceNumeric > 0 ? projectPriceNumeric : null
                };
            });

            let paymentPlan: PaymentPlan | null = null;
            if (plans && plans.length > 0) {
                const plan = plans[0];
                const { data: steps } = await supabase
                    .from('project_payment_steps')
                    .select('*')
                    .eq('plan_id', plan.id)
                    .order('step_order', { ascending: true });

                const downPayment = steps?.filter(s => String(s.stage_type || '').toLowerCase().includes('booking') || String(s.label || '').toLowerCase().includes('booking'))
                    .reduce((sum, s) => sum + (s.percentage || 0), 0) || 0;
                const duringConstruction = steps?.filter(s => String(s.stage_type || '').toLowerCase().includes('construction'))
                    .reduce((sum, s) => sum + (s.percentage || 0), 0) || 0;
                const onHandover = steps?.filter(s => String(s.stage_type || '').toLowerCase().includes('handover'))
                    .reduce((sum, s) => sum + (s.percentage || 0), 0) || 0;

                paymentPlan = {
                    name: plan.name || undefined,
                    downPayment,
                    duringConstruction,
                    onHandover,
                    total: (downPayment + duringConstruction + onHandover),
                    steps: steps?.map(s => ({
                        id: s.id,
                        name: s.label,
                        percentage: s.percentage,
                        stage_type: s.stage_type
                    }))
                };
            }

            const project = mapCanonicalRowToProject(row, media || [], paymentPlan, unitTypes);
            // Override developer logo with logo from developers table if available
            if (developerLogo) {
                project.developerLogo = developerLogo;
            }
            return project;
        } catch (err) {
            console.error('[MarketplaceAPI] Unexpected error in getProjectDetails:', err, 'ID/Slug:', idOrSlug);
            return null;
        }
    },

    /**
     * Background refresh for deep data
     * This allows updating cards with accurate unit-level data
     */
    refreshProjectDetail: async (id: string): Promise<CanonicalProject | null> => {
        // Trigger re-ingest for a single project if needed
        try {
            await fetch(getSupabaseUrl('functions/v1/ingest-reelly'), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
                    Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
                },
                body: JSON.stringify({ project_ids: [id] })
            });
        } catch (e) {
            console.warn('[MarketplaceAPI] Refresh ingest failed', e);
        }

        return MarketplaceAPI.getProjectDetails(id);
    }
};

const mapCanonicalRowToProject = (
    row: any,
    media: { url: string; media_type: string }[] = [],
    paymentPlan: PaymentPlan | null = null,
    unitTypes: UnitTypeOption[] = []
): CanonicalProject => {
    const cover = row.cover_image_url || media.find(m => m.media_type === 'cover')?.url || '';
    const gallery = media.filter(m => m.media_type === 'gallery').map(m => m.url);
    const brochureUrl = row.brochure_url || undefined;
    const floorPlans = media.filter(m => m.media_type === 'floorplan').map(m => m.url);
    const masterPlan = media.find(m => m.media_type === 'master_plan' || m.media_type === 'masterplan')?.url || null;

    const locationDisplay = row.location_display || [row.district, row.city || row.region].filter(Boolean).join(', ') || 'Dubai';
    const roiCalc = calculateROI(locationDisplay, row.developer || 'Unknown Developer', row.price_per_sqft || 0, row.completion_text || undefined);
    const roiProjected = row.roi_projected ?? roiCalc.roiValue;
    const roiDisplay = row.roi_display ?? roiCalc.roi;
    const roiTrend = row.roi_trend ?? (roiCalc.trend === 'up' ? 'up' : roiCalc.trend === 'down' ? 'down' : 'flat');

    const badgeUpdatable = ['ON_SALE', 'ON SALE', 'OFF-PLAN', 'ACTIVE', 'on_sale', 'active'].includes(String(row.sale_status || '').toUpperCase());
    let badgeText = (row.sale_status || 'OFF-PLAN').toString().replace(/_/g, ' ').toUpperCase();
    let badgeColor = 'blue';
    if (badgeText === 'OUT OF STOCK' || badgeText === 'OUT_OF_STOCK') {
        badgeText = 'SOLD OUT';
        badgeColor = 'slate';
    } else if (badgeText === 'ON SALE' || badgeText === 'ON_SALE') {
        badgeText = 'ON SALE';
        badgeColor = 'green';
    }

    if (row.payment_plan_summary && badgeUpdatable) {
        badgeText = row.payment_plan_summary;
        badgeColor = 'blue';
    }

    const priceNumeric = row.price_rounded_aed || 0;
    const priceDisplay = priceNumeric > 0 ? `AED ${Math.floor(priceNumeric).toLocaleString()}` : 'Price on Request';
    const sqftDisplay = row.sqft_display || (row.sqft_avg ? `${Math.ceil(Number(row.sqft_avg)).toLocaleString()} sqft` : 'TBD');

    return {
        id: row.id,
        slug: row.slug || undefined, // SEO-friendly slug
        title: row.title,
        developer: row.developer || 'Unknown Developer',
        developerLogo: row.developer_logo || undefined,
        location: {
            display: locationDisplay,
            district: row.district || undefined,
            city: row.city || undefined,
            region: row.region || undefined,
            coordinates: row.latitude && row.longitude ? {
                lat: Number(row.latitude),
                lng: Number(row.longitude),
                isGeneric: row.location_quality === 'LOW'
            } : undefined
        },
        pricing: {
            startingPriceNumeric: row.price_rounded_aed || null,
            startingPriceExact: row.starting_price_exact_aed || null,
            minPrice: row.min_price_aed || null,
            maxPrice: row.max_price_aed || null,
            startingPriceDisplay: priceDisplay,
            currency: row.price_currency || 'AED',
            source: row.price_source || 'unavailable',
            isRounded: true,
            hasDeepData: true
        },
        size: {
            minSqft: row.sqft_min ? Math.ceil(row.sqft_min) : null,
            maxSqft: row.sqft_max ? Math.ceil(row.sqft_max) : null,
            avgSqft: row.sqft_avg ? Math.ceil(row.sqft_avg) : null,
            display: sqftDisplay
        },
        pricePerSqft: (() => {
            // NEW FORMULA: min_price / min_sqft
            const minPrice = row.min_price_aed || null;
            const minSqft = row.sqft_min || null;
            let calculatedPsf: number | null = null;
            
            if (minPrice && minSqft && minPrice > 0 && minSqft > 0) {
                calculatedPsf = Math.round(minPrice / minSqft);
            }
            
            // Use calculated value, fallback to stored value if calculation fails
            const psf = calculatedPsf || row.price_per_sqft || null;
            const display = psf ? `AED ${psf.toLocaleString()}/sqft` : 'TBD';
            
            if (import.meta.env.DEV) {
                if (calculatedPsf) {
                    console.log(`[MarketplaceAPI] Calculated PSF for ${row.id}: ${calculatedPsf} (min_price: ${minPrice}, min_sqft: ${minSqft})`);
                }
                if (row.ai_about_opportunity) {
                    const summaryPreview = row.ai_about_opportunity.substring(0, 60);
                    console.log(`[MarketplaceAPI] Summary for ${row.id} (first 60 chars): "${summaryPreview}..."`);
                }
            }
            
            return {
                low: calculatedPsf || null,
                high: null,
                estimated: psf,
                display
            };
        })(),
        propertyType: {
            primary: row.property_type_primary || 'Unit details unavailable',
            types: row.property_type_types || [row.property_type_primary || 'Unit details unavailable'],
            source: row.property_type_source || 'unavailable',
            isDefaulted: row.property_type_is_defaulted || false
        },
        unitTypes,
        bedroomsAvailable: [...new Set(unitTypes.map(u => u.beds))].sort((a, b) => a - b),
        paymentPlan: paymentPlan,
        paymentPlanSummary: row.payment_plan_summary || null,
        status: {
            sale: row.sale_status || 'unknown',
            construction: row.construction_status || 'under_construction',
            completion: row.completion_text || 'TBD',
            completionTimestamp: row.completion_timestamp ? new Date(row.completion_timestamp).getTime() : null
        },
        media: {
            coverImage: cover,
            gallery: gallery,
            hasFloorPlans: floorPlans.length > 0,
            hasBrochure: !!brochureUrl,
            brochureUrl,
            floor_plans: floorPlans.length > 0 ? floorPlans : undefined,
            master_plan: masterPlan || undefined
        },
        content: {
            description: row.description || '',
            aboutOpportunity: row.ai_about_opportunity || null, // Read from DB column
            investmentSummary: row.investment_summary || '',
            features: row.features || []
        },
        roi: {
            projected: roiProjected || null,
            display: roiDisplay || 'TBD',
            trend: roiTrend === 'up' ? 'UP' : roiTrend === 'down' ? 'DOWN' : 'NEUTRAL'
        },
        metadata: {
            createdAt: row.source_created_at || row.created_at,
            updatedAt: row.source_updated_at || row.updated_at,
            lastSeen: row.last_seen || row.updated_at,
            unitsCount: row.units_count || 0,
            isMasterProject: row.is_master_project || false,
            escrowNumber: row.escrow_number || undefined
        },
        dataQuality: {
            hasDeepData: true,
            isPriceVerified: row.price_source === 'detail_units',
            missingFields: [],
            warnings: []
        },
        badge: {
            text: badgeText,
            color: badgeColor
        },
        price: priceDisplay,
        priceNumeric: priceNumeric,
        sqftNumeric: row.sqft_avg || 0,
        type: row.property_type_primary || 'Unit details unavailable',
        sqft: sqftDisplay,
        completion: row.completion_text || 'TBD',
        image: cover,
        images: gallery.length > 0 ? gallery : (cover ? [cover] : [])
    };
};
